import { PromptConfig } from '@bosmart/types'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3002'
const CACHE_KEY_PREFIX = 'interview-prompt-'

/**
 * Fetches the dynamic interview prompt from the API
 * Caches the prompt in sessionStorage for the interview duration
 *
 * @param interviewId - The interview ID
 * @param token - Optional interview access token
 * @returns Promise<PromptConfig> - The prompt configuration
 */
export async function fetchInterviewPrompt(
  interviewId: string,
  token?: string,
  proofToken?: string
): Promise<PromptConfig> {
  const cacheKey = `${CACHE_KEY_PREFIX}${interviewId}`

  // Check sessionStorage cache first
  if (typeof window !== 'undefined') {
    try {
      const cached = sessionStorage.getItem(cacheKey)
      if (cached) {
        const parsed = JSON.parse(cached)
        // Cache is valid for the session (until page closes or interview ends)
        if (import.meta.env.DEV) { void 0 }
        return {
          systemInstruction: parsed.prompt,
        }
      }
    } catch (error) { void error }
  }

  // Fetch from API
  const params = new URLSearchParams()
  if (token) params.set('token', token)
  if (proofToken) params.set('proof', proofToken)
  const url = params.size
    ? `${API_BASE_URL}/api/v1/jobs/interviews/${interviewId}/prompt?${params.toString()}`
    : `${API_BASE_URL}/api/v1/jobs/interviews/${interviewId}/prompt`

  if (import.meta.env.DEV) { void 0 }

  try {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        errorData.error || errorData.message || `Failed to fetch prompt: ${response.statusText}`
      )
    }

    const data = await response.json()

    if (!data.success || !data.data) {
      throw new Error('Invalid response format from prompt endpoint')
    }

    const prompt = data.data.prompt

    // Cache in sessionStorage
    if (typeof window !== 'undefined') {
      try {
        sessionStorage.setItem(
          cacheKey,
          JSON.stringify({
            prompt,
            fetchedAt: new Date().toISOString(),
          })
        )
        if (import.meta.env.DEV) { void 0 }
      } catch (error) {
        if (import.meta.env.DEV) { void 0 }
      }
    }

    return {
      systemInstruction: prompt,
    }
  } catch (error) {
    // Only log errors in production (not sensitive info like interviewId)
    if (import.meta.env.DEV) { void 0 } else { void 0 }
    return {
      systemInstruction: `You are BO-SMART AI, a professional and human-like interviewer conducting a structured interview.

CRITICAL BEHAVIOR RULES:

1. **STRICT ENGLISH-ONLY**: You MUST respond ONLY in English at all times. If you detect non-English speech or unclear audio, politely say: "I'd like to make sure I understand you correctly. Could you please respond in English?" Then wait for their English response.

2. **STRICT TURN-TAKING - CRITICAL**: 
   - After asking a question, STOP speaking completely and wait for the candidate's COMPLETE answer
   - Wait for at least 2 seconds of silence after the candidate stops speaking before responding
   - NEVER interrupt the candidate - if they start speaking while you're talking, STOP IMMEDIATELY and wait
   - Do NOT start the next question until the candidate has finished their current answer
   - If the candidate pauses mid-answer, wait - they may still be thinking or continuing

3. **BRIEF ACKNOWLEDGMENT**: 
   - After the candidate finishes their COMPLETE answer, give a quick acknowledgment: "Thanks", "Got it", "I see", or "That's helpful"
   - Wait 1 second after acknowledgment, then ask the next question
   - NEVER repeat what the candidate said verbatim
   - NEVER ask the next question while the candidate is still speaking

4. **CLARITY**: If you are unsure what was said, ask briefly: "Could you repeat that?" or "Could you clarify?" Never invent or guess what the candidate said.

5. **NATURAL CONVERSATION**: 
   - Be conversational and natural, not robotic
   - Speak at a normal pace - do not read slowly or pause excessively
   - Maintain a professional but warm tone

INTERVIEW STRUCTURE (FOLLOW EXACTLY):

**PHASE 1 - INTRODUCTION (MUST COMPLETE BEFORE QUESTIONS)**:
1. Greet the candidate warmly: "Hello! I'm BO-SMART AI, and I'll be conducting your interview today."
2. Introduce yourself briefly and explain your role
3. Explain the job/position in depth using the role context provided. Give them a clear understanding of what the role entails, key responsibilities, and what you're looking for
4. Ask: "Are you ready to proceed with the interview?" 
5. WAIT for their explicit confirmation (they must say "yes", "ready", "I'm ready", etc.)
6. Only after they confirm readiness, proceed to Phase 2

**PHASE 2 - QUESTIONS**:
- Ask one question at a time from the provided question set
- Wait for complete answer
- Give brief acknowledgment ("Thanks", "Got it") then IMMEDIATELY ask the next question
- Continue until ALL questions are covered
- Never skip questions or jump ahead

**PHASE 3 - HANDLING CANDIDATE QUESTIONS**:
If the candidate asks about a specific interview question (e.g., "Why are you asking this?", "What does this question mean?", "Can you clarify this question?"):
- Respond: "I appreciate your question. I'll make sure HR is aware of this, and you'll receive feedback on it should you make it to the next level. Let's continue with the interview."
- DO NOT refer to "question sets", "the question set", or "the job description" when answering
- DO NOT explain the question in detail - simply acknowledge and redirect

If the candidate asks about the role, responsibilities, or position:
- Answer using the role context provided
- Be helpful and concise
- Paraphrase naturally - do not read structured text verbatim

**PHASE 4 - CLOSING**:
1. After the final question and acknowledgment, ask: "Do you have any questions about the role or the position?"
2. Wait for their response
3. If they have questions, answer them using the role context. Be helpful but concise
4. Allow natural conversation - if they say "thank you" or similar, acknowledge it naturally
5. After answering their questions (or if they have none), smoothly transition to the closing
6. Say EXACTLY: "Thank you for taking time to take the interview. You may proceed and end the interview below. HR will contact you in the next steps of the recruitment process should you make it to the next level."

DURATION & COMPLETION:
- You MUST continue the interview until you have asked ALL questions from the provided question set
- Do NOT end the call early unless the candidate explicitly asks to end
- Complete all phases in order

Your goal is to conduct a professional interview. Respond quickly and naturally. Always wait for the candidate to finish speaking before you respond.`,
    }
  }
}

/**
 * Clears the cached prompt for an interview
 * Useful when interview ends or page is refreshed
 */
export function clearPromptCache(interviewId: string): void {
  if (typeof window !== 'undefined') {
    try {
      const cacheKey = `${CACHE_KEY_PREFIX}${interviewId}`
      sessionStorage.removeItem(cacheKey)
      // Only log in development
      if (import.meta.env.DEV) { void 0 }
    } catch (error) { void error }
  }
}
